import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useMe, useMembers, useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import type { TenantRole } from '../types';

/** Настройки трекера: участники и справочники. Очереди живут в своём разделе. */
export function SettingsPage() {
  const { tenantId = '' } = useParams();
  const catalog = useTenantCatalog(tenantId);
  const members = useMembers(tenantId);
  const me = useMe();
  const queryClient = useQueryClient();
  const [addingMember, setAddingMember] = useState(false);

  const tenant = me.data?.tenants.find((t) => t.id === tenantId);
  const canManage = tenant?.myRole === 'OWNER' || tenant?.myRole === 'ADMIN';

  return (
    <>
      <div className="page-head">
        <h1>Настройки</h1>
      </div>

      <div className="col">
        <div className="card">
          <div className="card-head">
            <h2>Трекер</h2>
          </div>
          <div className="grid-3">
            <div>
              <div className="lbl">Название</div>
              <div className="val">{tenant?.name ?? '—'}</div>
            </div>
            <div>
              <div className="lbl">Владелец</div>
              <div className="val">
                {tenant?.ownerType === 'COMPANY' ? 'Организация' : 'Частное лицо'}
              </div>
            </div>
            <div>
              <div className="lbl">Ваша роль</div>
              <div className="val">{tenant ? ROLE_LABELS[tenant.myRole] ?? tenant.myRole : '—'}</div>
            </div>
          </div>
          <div className="form-hint">
            Трекер заводится сам при первом входе с ролью — отдельно создавать его не нужно.
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Очереди</h2>
            <Link className="card-link" to={`/t/${tenantId}/queues`}>
              Все очереди
            </Link>
          </div>
          {catalog.queues.length === 0 ? (
            <div className="muted small">Очередей пока нет.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Ключ</th>
                  <th>Название</th>
                  <th>Задач заведено</th>
                </tr>
              </thead>
              <tbody>
                {catalog.queues.map((queue) => (
                  <tr key={queue.id}>
                    <td>
                      <span className="queue-key">{queue.key}</span>
                    </td>
                    <td>
                      <Link to={`/t/${tenantId}/queues/${queue.id}`}>{queue.name}</Link>
                      {queue.archived && <span className="badge badge-gray" style={{ marginLeft: 6 }}>архив</span>}
                    </td>
                    <td>{queue.issueCounter}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Участники</h2>
            {canManage && (
              <button className="btn btn-secondary btn-sm" onClick={() => setAddingMember(true)}>
                Добавить
              </button>
            )}
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Роль</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.data?.content.map((member) => (
                <tr key={member.id}>
                  <td>
                    <code className="small">{member.userId}</code>
                  </td>
                  <td>
                    <span className="badge badge-gray">{ROLE_LABELS[member.role] ?? member.role}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {canManage && member.role !== 'OWNER' && (
                      <button
                        className="btn btn-secondary btn-xs"
                        onClick={async () => {
                          await api.del(`/tenants/${tenantId}/members/${member.id}`);
                          await queryClient.invalidateQueries({ queryKey: ['members', tenantId] });
                        }}
                      >
                        Исключить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="form-hint">
            Сотрудники организации попадают в трекер сами, когда входят со своей ролью. Вручную
            добавляют тех, кого в организации нет.
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Справочники</h2>
          </div>
          <div className="grid-3">
            <div>
              <div className="lbl">Статусы</div>
              {catalog.statuses.map((status) => (
                <div key={status.id} className="small" style={{ marginTop: 4 }}>
                  <span className={`badge ${STATUS_BADGE[status.category]}`}>{status.name}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="lbl">Типы задач</div>
              {catalog.types.map((type) => (
                <div key={type.id} className="small" style={{ marginTop: 4 }}>
                  {type.name}
                </div>
              ))}
            </div>
            <div>
              <div className="lbl">Приоритеты</div>
              {catalog.priorities.map((priority) => (
                <div key={priority.id} className="small" style={{ marginTop: 4 }}>
                  {priority.name}
                </div>
              ))}
            </div>
          </div>
          <div className="form-hint">
            Справочники заводятся при создании трекера и правятся через API — редактор появится позже.
          </div>
        </div>
      </div>

      {addingMember && <AddMemberModal tenantId={tenantId} onClose={() => setAddingMember(false)} />}
    </>
  );
}

function AddMemberModal({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<TenantRole>('MEMBER');

  const add = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/members`, { userId, role }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['members', tenantId] });
      onClose();
    },
  });

  return (
    <Modal title="Добавить участника" onClose={onClose}>
      <div className="form-group">
        <label className="form-label" htmlFor="member-id">
          Идентификатор пользователя
        </label>
        <input
          id="member-id"
          className="input"
          value={userId}
          onChange={(event) => setUserId(event.target.value.trim())}
          placeholder="UUID из сервиса subjects"
          autoFocus
        />
        <div className="form-hint">
          Поиска по людям здесь пока нет: трекер знает только идентификаторы, каталог живёт
          в subjects.
        </div>
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="member-role">
          Роль
        </label>
        <select
          id="member-role"
          className="input"
          value={role}
          onChange={(event) => setRole(event.target.value as TenantRole)}
        >
          <option value="ADMIN">Администратор — настройки трекера</option>
          <option value="MEMBER">Участник — работа с задачами</option>
          <option value="VIEWER">Наблюдатель — только чтение</option>
        </select>
      </div>

      {add.isError && <div className="form-error">{(add.error as ApiError).message}</div>}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>
          Отмена
        </button>
        <button className="btn btn-primary" disabled={!userId || add.isPending} onClick={() => add.mutate()}>
          Добавить
        </button>
      </div>
    </Modal>
  );
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'владелец',
  ADMIN: 'администратор',
  MEMBER: 'участник',
  VIEWER: 'наблюдатель',
};

const STATUS_BADGE: Record<string, string> = {
  TODO: 'badge-gray',
  IN_PROGRESS: 'badge-blue',
  DONE: 'badge-green',
};
